/**
 * Deterministic fingerprint of the feature registry.
 *
 * The working contract (§1) fixes sha256 over the canonical JSON, and §2 binds the server side
 * to it (`src/portalGuide/registry.meta.json` carries the same value). That is why this is a
 * pure, synchronous sha256 implementation instead of an environment dependency: the registry
 * runs in the browser (CRA) and in Node (sync script), Web Crypto is asynchronous and only
 * available under https/localhost, and `node:crypto` does not exist in the browser at all.
 *
 * Computation is done in 32-bit words; the result is 64 hex characters. It is a change
 * indicator, not a security feature.
 */

/** UTF-8 bytes of a string — without TextEncoder, so jsdom environments without that global work. */
function utf8Bytes(text: string): Uint8Array {
  // Worst case is three bytes per UTF-16 unit (a surrogate pair is two units for four bytes).
  const bytes = new Uint8Array(text.length * 3);
  let length = 0;
  for (let index = 0; index < text.length; index += 1) {
    let code = text.charCodeAt(index);
    if (code >= 0xd800 && code <= 0xdbff && index + 1 < text.length) {
      const next = text.charCodeAt(index + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        code = (code - 0xd800) * 0x400 + (next - 0xdc00) + 0x10000;
        index += 1;
      }
    }
    if (code < 0x80) {
      bytes[length++] = code;
    } else if (code < 0x800) {
      bytes[length++] = 0xc0 | (code >> 6);
      bytes[length++] = 0x80 | (code & 0x3f);
    } else if (code < 0x10000) {
      bytes[length++] = 0xe0 | (code >> 12);
      bytes[length++] = 0x80 | ((code >> 6) & 0x3f);
      bytes[length++] = 0x80 | (code & 0x3f);
    } else {
      bytes[length++] = 0xf0 | (code >> 18);
      bytes[length++] = 0x80 | ((code >> 12) & 0x3f);
      bytes[length++] = 0x80 | ((code >> 6) & 0x3f);
      bytes[length++] = 0x80 | (code & 0x3f);
    }
  }
  return bytes.subarray(0, length);
}

/** Round constants: the first 32 bits of the fractional parts of the cube roots of the first 64 primes. */
const K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];

function rotateRight(value: number, amount: number): number {
  return (value >>> amount) | (value << (32 - amount));
}

/** sha256 as 64 hex characters. Pure function, no environment dependency. */
export function sha256Hex(text: string): string {
  const encoded = utf8Bytes(text);
  const bitLength = encoded.length * 8;

  // Padding: 0x80, then zeros until length ≡ 56 (mod 64), then the bit length as a 64-bit number.
  const bytes = new Uint8Array(Math.ceil((encoded.length + 9) / 64) * 64);
  bytes.set(encoded);
  bytes[encoded.length] = 0x80;
  const high = Math.floor(bitLength / 0x100000000);
  const low = bitLength >>> 0;
  const end = bytes.length - 8;
  bytes[end] = (high >>> 24) & 0xff;
  bytes[end + 1] = (high >>> 16) & 0xff;
  bytes[end + 2] = (high >>> 8) & 0xff;
  bytes[end + 3] = high & 0xff;
  bytes[end + 4] = (low >>> 24) & 0xff;
  bytes[end + 5] = (low >>> 16) & 0xff;
  bytes[end + 6] = (low >>> 8) & 0xff;
  bytes[end + 7] = low & 0xff;

  // Initial values: the first 32 bits of the fractional parts of the square roots of the first eight primes.
  const state = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  const words = new Uint32Array(64);

  for (let block = 0; block < bytes.length; block += 64) {
    for (let index = 0; index < 16; index += 1) {
      const at = block + index * 4;
      words[index] = (bytes[at] << 24) | (bytes[at + 1] << 16) | (bytes[at + 2] << 8) | bytes[at + 3];
    }
    for (let index = 16; index < 64; index += 1) {
      const a = words[index - 15];
      const b = words[index - 2];
      const s0 = rotateRight(a, 7) ^ rotateRight(a, 18) ^ (a >>> 3);
      const s1 = rotateRight(b, 17) ^ rotateRight(b, 19) ^ (b >>> 10);
      words[index] = words[index - 16] + s0 + words[index - 7] + s1;
    }

    // Plain int32 arithmetic (`| 0`); the Uint32Array stores wrap modulo 2^32 on their own.
    let a = state[0];
    let b = state[1];
    let c = state[2];
    let d = state[3];
    let e = state[4];
    let f = state[5];
    let g = state[6];
    let h = state[7];
    for (let index = 0; index < 64; index += 1) {
      const s1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
      const choose = (e & f) ^ (~e & g);
      const temp1 = (h + s1 + choose + K[index] + words[index]) | 0;
      const s0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
      const majority = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (s0 + majority) | 0;

      h = g;
      g = f;
      f = e;
      e = (d + temp1) | 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) | 0;
    }

    state[0] += a;
    state[1] += b;
    state[2] += c;
    state[3] += d;
    state[4] += e;
    state[5] += f;
    state[6] += g;
    state[7] += h;
  }

  let hex = '';
  for (let index = 0; index < 8; index += 1) {
    hex += `0000000${state[index].toString(16)}`.slice(-8);
  }
  return hex;
}

/**
 * Canonical JSON: object keys sorted, no whitespace, `undefined` fields omitted.
 * Two registries with the same content produce the same text, regardless of the order
 * in which the fields were written.
 */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value === undefined ? null : value);
  }
  if (Array.isArray(value)) {
    return `[${value.map((item) => canonicalJson(item)).join(',')}]`;
  }
  const record = value as Record<string, unknown>;
  const parts: string[] = [];
  Object.keys(record)
    .sort()
    .forEach((key) => {
      if (record[key] === undefined) {
        return;
      }
      parts.push(`${JSON.stringify(key)}:${canonicalJson(record[key])}`);
    });
  return `{${parts.join(',')}}`;
}
